import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, Loader2, PenLine, LayoutGrid, List, Trash2, XCircle, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { api, DAY_LABELS } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { confirmDialog } from '@/components/ui/confirm-dialog';
import { getMySubstituteToday, mergeTodaySchedule } from '@/lib/guruPenggantiSchedule';
import SubstituteBadge from '@/components/guru-pengganti/SubstituteBadge';
import SubstituteJournalDialog from '@/components/guru-pengganti/SubstituteJournalDialog';
import ReplacedSlotNote from '@/components/guru-pengganti/ReplacedSlotNote';

export default function JadwalPage() {
  const { user, activeRole } = useAuth();
  const [today, setToday] = useState([]);
  // Slot hasil penugasan guru pengganti hari ini (wali kelas: ditampilkan terpisah dari jadwal kelas).
  const [substituteToday, setSubstituteToday] = useState([]);
  // Slot yang sedang diisi jurnalnya: mode 'substitute' (saya pengganti) | 'original' (slot saya sedang digantikan).
  const [journalTarget, setJournalTarget] = useState(null);
  const markJournalFilled = (slot) => {
    const same = (x) => (journalTarget?.mode === 'original'
      ? !x.is_substitute && x.id === slot.id
      : x.assignment_id && x.assignment_id === slot.assignment_id);
    const mark = (list) => list.map((x) => (same(x) ? { ...x, journal_filled: true } : x));
    setToday(mark);
    setSubstituteToday(mark);
  };
  const fillSubstitute = (slot) => setJournalTarget({ slot, mode: 'substitute' });
  const fillOriginal = (slot) => setJournalTarget({ slot, mode: 'original' });
  const [weekly, setWeekly] = useState([]);
  const [weeklyGrouped, setWeeklyGrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('card'); // card | list

  // Sorting state for list view
  const [sortColumn, setSortColumn] = useState('day'); // day, jam, jtm, class, subject, teacher, room
  const [sortDirection, setSortDirection] = useState('asc'); // asc, desc

  const loadSchedules = async () => {
    try {
      if (activeRole === 'siswa') {
        const { data } = await api.get(`/student/${user.id}/today`);
        setToday(data?.today_schedule || []);
        if (data?.class?.id) {
          const { data: all } = await api.get('/schedules', { params: { class_id: data.class.id } });
          setWeekly(all);
          const { data: grouped } = await api.get('/schedules/grouped', { params: { class_id: data.class.id } });
          setWeeklyGrouped(grouped || []);
        }
      } else if (activeRole === 'wali_kelas') {
        // Wali kelas: get jadwal kelasnya
        // Get active academic year first
        const ayRes = await api.get('/academic-years/active');
        const activeAY = ayRes.data;

        const { data: classes } = await api.get('/classes', {
          params: activeAY ? { academic_year_id: activeAY.id } : {}
        });
        const myClass = classes.find(c => c.homeroom_teacher_id === user.id);
        setSubstituteToday(await getMySubstituteToday().catch(() => []));
        if (myClass) {
          const { data: todayData } = await api.get('/schedules', { params: { class_id: myClass.id, day: getDayId() } });
          setToday(todayData || []);
          const { data: all } = await api.get('/schedules', { params: { class_id: myClass.id } });
          setWeekly(all || []);
          const { data: grouped } = await api.get('/schedules/grouped', { params: { class_id: myClass.id } });
          setWeeklyGrouped(grouped || []);
        } else {
          setToday([]);
          setWeekly([]);
          setWeeklyGrouped([]);
        }
      } else {
        // Guru: jadwal mengajar mereka sendiri + slot yang ditugaskan sebagai guru pengganti
        const [{ data }, substitute] = await Promise.all([
          api.get('/schedules/my-today'),
          getMySubstituteToday().catch(() => []),
        ]);
        setToday(mergeTodaySchedule(data, substitute));
        const { data: all } = await api.get('/schedules', { params: { teacher_id: user.id } });
        setWeekly(all);
        const { data: grouped } = await api.get('/schedules/grouped', { params: { teacher_id: user.id } });
        setWeeklyGrouped(grouped || []);
      }
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, activeRole]);

  const handleUnsubmit = async (s) => {
    if (!(await confirmDialog('Batalkan pengiriman jadwal ini? Jadwal akan kembali ke status draft.'))) return;
    try {
      await api.put(`/schedules/${s.id}/unsubmit`);
      toast.success('Jadwal dibatalkan dan kembali ke draft');
      await loadSchedules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal membatalkan');
    }
  };

  const handleDelete = async (s) => {
    if (!(await confirmDialog('Hapus jadwal ini?'))) return;
    try {
      await api.delete(`/schedules/${s.id}`);
      toast.success('Jadwal dihapus');
      await loadSchedules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  // Helper function to get current day ID (senin, selasa, etc)
  function getDayId() {
    const days = ['minggu', 'senin', 'selasa', 'rabu', 'kamis', 'jumat', 'sabtu'];
    return days[new Date().getDay()];
  }

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort weeklyGrouped items based on current sort column and direction
  const sortedWeeklyGrouped = [...weeklyGrouped].sort((a, b) => {
    let aVal, bVal;

    switch (sortColumn) {
      case 'day':
        // Sort by day order
        const dayOrder = ['senin', 'selasa', 'rabu', 'kamis', 'jumat', 'sabtu', 'minggu'];
        aVal = dayOrder.indexOf(a.day?.toLowerCase() || '');
        bVal = dayOrder.indexOf(b.day?.toLowerCase() || '');
        break;
      case 'jam':
        // Sort by start_time
        aVal = a.start_time || '';
        bVal = b.start_time || '';
        break;
      case 'jtm':
        // Sort by jtm_count
        aVal = a.jtm_count || 1;
        bVal = b.jtm_count || 1;
        break;
      case 'class':
        // Sort by class_name
        aVal = (a.class_name || '').toLowerCase();
        bVal = (b.class_name || '').toLowerCase();
        break;
      case 'subject':
        // Sort by subject_name
        aVal = (a.subject_name || '').toLowerCase();
        bVal = (b.subject_name || '').toLowerCase();
        break;
      case 'teacher':
        // Sort by teacher_name
        aVal = (a.teacher_name || '').toLowerCase();
        bVal = (b.teacher_name || '').toLowerCase();
        break;
      case 'room':
        // Sort by room_name
        aVal = (a.room_name || '').toLowerCase();
        bVal = (b.room_name || '').toLowerCase();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Group weekly by day
  const byDay = (weekly || []).reduce((acc, s) => {
    (acc[s.day] = acc[s.day] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <Badge className="bg-[#006837]/10 text-[#006837] border-[#006837]/20 mb-2">Jadwal Pelajaran</Badge>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Jadwal Saya</h1>
      </div>
      <Tabs defaultValue="today">
        <TabsList>
          <TabsTrigger value="today" data-testid="jadwal-tab-today">Hari Ini ({today.length + substituteToday.length})</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="jadwal-tab-weekly">Mingguan ({weekly.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="today" className="mt-4">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <div className="space-y-4">
              <ScheduleList items={today} onFillSubstitute={fillSubstitute} onFillOriginal={fillOriginal} />
              {substituteToday.length > 0 && (
                <div data-testid="jadwal-substitute-today">
                  <div className="text-sm font-semibold text-slate-700 mb-2">Menggantikan Guru Hari Ini</div>
                  <ScheduleList items={substituteToday} onFillSubstitute={fillSubstitute} />
                </div>
              )}
            </div>
          )}
        </TabsContent>
        <TabsContent value="weekly" className="mt-4 space-y-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-slate-600">
              {viewMode === 'list' && (
                <span className="px-2 py-1 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
                  <span className="font-semibold">JTM (Jam Tugas Mengajar):</span> Jam mengajar berdekatan digabung
                </span>
              )}
            </div>
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList>
                <TabsTrigger value="card" data-testid="view-mode-card"><LayoutGrid className="h-4 w-4 mr-1" /> Card</TabsTrigger>
                <TabsTrigger value="list" data-testid="view-mode-list"><List className="h-4 w-4 mr-1" /> List</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {viewMode === 'card' ? (
            <>
              {['senin', 'selasa', 'rabu', 'kamis', 'jumat', 'sabtu'].map((day) => (
                <div key={day}>
                  <div className="text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wide">{DAY_LABELS[day]}</div>
                  <ScheduleList items={byDay[day] || []} />
                </div>
              ))}
            </>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table data-testid="jadwal-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('day')}>
                          <div className="flex items-center gap-1">
                            Hari
                            {sortColumn === 'day' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('jam')}>
                          <div className="flex items-center gap-1">
                            Jam
                            {sortColumn === 'jam' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('jtm')}>
                          <div className="flex items-center gap-1">
                            JTM
                            {sortColumn === 'jtm' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('class')}>
                          <div className="flex items-center gap-1">
                            Kelas
                            {sortColumn === 'class' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('subject')}>
                          <div className="flex items-center gap-1">
                            Mapel
                            {sortColumn === 'subject' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('teacher')}>
                          <div className="flex items-center gap-1">
                            Guru
                            {sortColumn === 'teacher' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('room')}>
                          <div className="flex items-center gap-1">
                            Ruang
                            {sortColumn === 'room' ? (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                          </div>
                        </TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedWeeklyGrouped.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell className="capitalize">{DAY_LABELS[s.day]}</TableCell>
                          <TableCell className="font-mono">
                            <div>{s.hour_range || `Jam ke-${s.slot_index + 1 || '?'}`}</div>
                            <div className="text-[10px] text-slate-500">{s.time_range || `${s.start_time}-${s.end_time}`}</div>
                          </TableCell>
                          <TableCell className="font-semibold text-[#006837]">
                            {s.jtm_count || 1} JTM
                          </TableCell>
                          <TableCell className="font-semibold">{s.class_name || '-'}</TableCell>
                          <TableCell>{s.subject_name || '-'}</TableCell>
                          <TableCell className="text-sm">{s.teacher_name || '-'}</TableCell>
                          <TableCell className="font-mono text-sm">{s.room_name || '-'}</TableCell>
                          <TableCell>
                            {s.status === 'locked' ? (
                              <Badge className="bg-rose-100 text-rose-700 border-rose-200">Terkunci</Badge>
                            ) : s.status === 'approved' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Disetujui</Badge>
                            ) : s.status === 'submitted' ? (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200">Terkirim</Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-600 border-amber-300">Draft</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {s.status === 'submitted' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleUnsubmit(s)}
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  title="Batal Kirim"
                                  data-testid={`unsubmit-${s.id}`}
                                >
                                  <XCircle className="h-4 w-4 mr-1" /> Batal Kirim
                                </Button>
                              )}
                              {s.status === 'approved' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDelete(s)}
                                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                  title="Hapus"
                                  data-testid={`delete-${s.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" /> Hapus
                                </Button>
                              )}
                              {s.status === 'locked' && (
                                <span className="text-xs text-slate-500 italic">Terkunci</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      <SubstituteJournalDialog
        slot={journalTarget?.slot}
        mode={journalTarget?.mode}
        open={!!journalTarget}
        onOpenChange={(o) => { if (!o) setJournalTarget(null); }}
        onSaved={markJournalFilled}
      />
    </div>
  );
}

function ScheduleList({ items, onFillSubstitute, onFillOriginal }) {
  if (!items || items.length === 0) {
    return <div className="text-sm text-slate-500 italic py-4">Tidak ada jadwal</div>;
  }
  return (
    <div className="space-y-2">
      {items.map((s, idx) => (
        <div
          key={s.assignment_id || s.id || idx}
          className={`flex items-center gap-3 rounded-xl border p-3 ${s.is_substitute ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-white'}`}
          data-testid={s.is_substitute ? `jadwal-substitute-${s.assignment_id}` : undefined}
        >
          <div className="font-mono text-sm font-semibold w-24 shrink-0">{s.start_time}-{s.end_time}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="font-semibold text-slate-900 truncate">{s.subject_name}</div>
              {s.is_substitute && <SubstituteBadge originalTeacherName={s.original_teacher_name} className="shrink-0" />}
            </div>
            {s.is_substitute ? (
              <div className="text-xs text-slate-600">
                {s.class_name} • {s.room_name} • Menggantikan <span className="font-medium">{s.original_teacher_name}</span>
              </div>
            ) : (
              <>
                <div className="text-xs text-slate-600">{s.class_name} • {s.room_name} • {s.teacher_name}</div>
                <ReplacedSlotNote substitute={s.substitute} />
              </>
            )}
          </div>
          {s.journal_filled ? (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Terisi</Badge>
          ) : !s.is_substitute && s.substitute && onFillOriginal ? (
            <Button
              size="sm"
              variant="outline"
              className="gap-1 shrink-0"
              onClick={() => onFillOriginal(s)}
              data-testid={`fill-original-${s.id}`}
            >
              <PenLine className="h-3.5 w-3.5" /> Isi Jurnal
            </Button>
          ) : s.is_substitute && onFillSubstitute && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1 border-amber-300 text-amber-800 hover:bg-amber-50 shrink-0"
              onClick={() => onFillSubstitute(s)}
              data-testid={`fill-substitute-${s.assignment_id}`}
            >
              <PenLine className="h-3.5 w-3.5" /> Isi Jurnal
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
